import { useMyMutation, MutationInfos, client, Options } from '../helpers';
import {
    CreateGroupInput,
    DeleteGroupInput,
    EditGroupInput,
    AddUserToGroupInput,
    DeleteUserFromGroupInput,
    CreateUserInput,
    DeleteUserInput,
    CreateAndAddUserToGroupsInput,
    ApiType,
    SendNewInviteInput,
    UpdateUser,
} from '../../apiTypes';
import { typeContract } from './types';
import { stringsRes } from '../../../resources/strings';
import { ToastWithIcon } from '../../../navigation/routes/toastProvider';

// Mutation function

//= =========================//
//= ======== Group ==========//
//= =========================//

function updateGroup(variables: EditGroupInput): MutationInfos {
    const { id: contractId, groupId, ...rest } = variables;
    return {
        method: 'put',
        path: `/api/contract/${contractId}/group/${groupId}`,
        apiType: ApiType.ID,
        type: typeContract.UPDATE_GROUP,
        params: {
            ...rest,
        },
    };
}

function deleteGroup(variables: DeleteGroupInput): MutationInfos {
    const { id: contractId, groupId, ...rest } = variables;
    return {
        method: 'delete',
        path: `/api/contract/${contractId}/group/${groupId}`,
        apiType: ApiType.ID,
        type: typeContract.DELETE_GROUP,
        params: {
            ...rest,
        },
    };
}

function createGroup(variables: CreateGroupInput): MutationInfos {
    const { id: contractId, ...rest } = variables;
    return {
        method: 'post',
        path: `/api/contract/${contractId}/group`,
        apiType: ApiType.ID,
        type: typeContract.CREATE_GROUP,
        params: {
            ...rest,
        },
    };
}

function addUserToGroup(variables: AddUserToGroupInput): MutationInfos {
    const { id: contractId, groupId, users } = variables;
    return {
        method: 'post',
        path: `/api/contract/${contractId}/group/${groupId}/user`,
        apiType: ApiType.ID,
        type: typeContract.ADD_USERS_TO_GROUP,
        params: { users },
    };
}

function editUserRolesInGroup(variables: AddUserToGroupInput): MutationInfos {
    const { id: contractId, groupId, users } = variables;
    return {
        method: 'put',
        path: `/api/contract/${contractId}/group/${groupId}/user`,
        apiType: ApiType.ID,
        type: typeContract.EDIT_USERS_ROLES_IN_GROUP,
        params: { users },
    };
}

function deleteUserFromGroup(variables: DeleteUserFromGroupInput): MutationInfos {
    const { id: contractId, groupId, userId } = variables;
    return {
        method: 'delete',
        path: `/api/contract/${contractId}/group/${groupId}/user/${userId}`,
        apiType: ApiType.ID,
        type: typeContract.ADD_USERS_TO_GROUP,
        params: {},
    };
}

//= ========================//
//= ======== User ==========//
//= ========================//

function createUser(variables: CreateUserInput): MutationInfos {
    const { id: contractId, groupId, ...rest } = variables;
    return {
        method: 'post',
        path: `/api/contract/${contractId}/group/${groupId}/invite`,
        apiType: ApiType.ID,
        type: typeContract.CREATE_USER,
        params: { ...rest },
    };
}

function updateUser(variables: UpdateUser): MutationInfos {
    const { userId: id, ...rest } = variables;
    return {
        method: 'put',
        path: `/api/user/${id}`,
        apiType: ApiType.ID,
        type: typeContract.CREATE_USER,
        params: { ...rest },
    };
}

function deleteUser(variables: DeleteUserInput): MutationInfos {
    const { id: contractId, ...rest } = variables;
    return {
        method: 'delete',
        path: `/api/contract/${contractId}/user`,
        apiType: ApiType.ID,
        type: typeContract.DELETE_USER,
        params: { ...rest },
    };
}

function createAndAddUserToGroups(variables: CreateAndAddUserToGroupsInput): MutationInfos {
    const { id: contractId, users } = variables;
    return {
        method: 'post',
        path: `/api/contract/${contractId}/coworkers`,
        apiType: ApiType.ID,
        type: typeContract.ADD_USERS_TO_GROUPS,
        params: { users },
    };
}

function sendNewInviteEmail(variables: SendNewInviteInput): MutationInfos {
    const { id: contractId, groupId, userId } = variables;
    return {
        method: 'post',
        path: `/api/contract/${contractId}/group/${groupId}/send-email-invitation/${userId}`,
        apiType: ApiType.ID,
        type: typeContract.SEND_NEW_INVITE_EMAIL,
        params: {},
    };
}

//= =========================//
//= ======== Hooks ==========//
//= =========================//

export function useUpdateGroup(options?: Options<EditGroupInput>) {
    return useMyMutation(updateGroup, {
        onSuccess: (_, variables) => {
            client.resetQueries(`groups${variables.id}`);
        },
        ...options,
    });
}

export function useDeleteGroup() {
    return useMyMutation(deleteGroup, {
        onSuccess: (_, variables) => {
            client.resetQueries(`groups${variables.id}`);
            client.resetQueries(`collabs${variables.id}`);
        },
    });
}

export function useCreateGroup(options?: Options<CreateGroupInput>) {
    return useMyMutation(createGroup, {
        onSuccess: (_, variables) => {
            client.resetQueries(`groups${variables.id}`);
        },
        ...options,
    });
}

export function useCreateUser(options?: Options<CreateUserInput>) {
    return useMyMutation(createUser, {
        onSuccess: (_, variables) => {
            client.resetQueries(`collabs${variables.id}`);
            client.resetQueries(`groups${variables.id}`);
            ToastWithIcon('info', stringsRes.admin.users.collabAdded);
        },
        ...options,
    });
}

export function useDeleteUser(options?: Options<DeleteUserInput>) {
    return useMyMutation(deleteUser, {
        onSuccess: (_, variables) => {
            client.resetQueries(`collabs${variables.id}`);
            client.resetQueries(`groups${variables.id}`);
            ToastWithIcon('error', stringsRes.admin.users.collabDeleted);
        },
        ...options,
    });
}

/**
 * Add existing user(s) and to one group
 * @param options payload
 * @returns mutation Hook
 */
export function useAddUserToGroup(options?: Options<AddUserToGroupInput>) {
    return useMyMutation(addUserToGroup, {
        onSuccess: (_, variables) => {
            client.resetQueries(`collabs${variables.id}`);
            client.resetQueries(`groups${variables.id}`);
        },
        ...options,
    });
}

/**
 * Send the invitation again for one user, group and contract
 * @param options payload
 * @returns mutation Hook
 */
export function useSendNewInviteEmail(options?: Options<SendNewInviteInput>) {
    return useMyMutation(sendNewInviteEmail, {
        onSuccess: () => {
            ToastWithIcon('info', stringsRes.admin.users.inviteSent);
        },
        ...options,
    });
}

/**
 * Create user(s) and add them to the group(s) given
 * @param options payload
 * @returns mutation Hook
 */
export function useCreateAndAddUsersToGroups(options?: Options<CreateAndAddUserToGroupsInput>) {
    return useMyMutation(createAndAddUserToGroups, {
        onSuccess: (_, variables) => {
            client.resetQueries(`collabs${variables.id}`);
            client.resetQueries(`groups${variables.id}`);
        },
        ...options,
    });
}

export function useUpdateUser(options?: Options<UpdateUser>) {
    return useMyMutation(updateUser, {
        onSuccess: (_, variables) => {
            client.resetQueries(`collabs${variables.userId}`);
            client.resetQueries(`groups${variables.group}`);
        },
        ...options,
    });
}

export function useEditUsersRolesInGroup(options?: Options<AddUserToGroupInput>) {
    return useMyMutation(editUserRolesInGroup, {
        onSuccess: (_, variables) => {
            client.resetQueries(`collabs${variables.id}`);
            client.resetQueries(`groups${variables.id}`);
            ToastWithIcon('info', stringsRes.common.edited);
        },
        ...options,
    });
}

export function useDeleteUserFromGroup(options?: Options<DeleteUserFromGroupInput>) {
    return useMyMutation(deleteUserFromGroup, {
        onSuccess: (_, variables) => {
            client.resetQueries(`collabs${variables.id}`);
            client.resetQueries(`groups${variables.id}`);
        },
        ...options,
    });
}
